import { RangePickerProps } from "@/components/Inputs/RangePicker/RangePickerProps.ts";
import { DayButton } from "@/components/Inputs/RangePicker/components/DayButton/DayButton.tsx";
import { DayPickerButton } from "@/components/Inputs/RangePicker/components/DayPickerButton/DayPickerButton.tsx";
import { Footer } from "@/components/Inputs/RangePicker/components/Footer/Footer.tsx";
import { getDateBoundary } from "@/components/Inputs/RangePicker/helpers/getDateBoundary.ts";
import { useHoverStore } from "@/components/Inputs/RangePicker/stores/useHoverStore.ts";
import { filterDateDefault } from "@/helpers/filterDates.ts";
import { locales } from "@/helpers/locales.ts";
import { isAfter, isBefore, isSameDay } from "date-fns";
import format from "date-fns/format";
import i18n from "i18next";
import capitalize from "lodash/capitalize";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DayPicker, DayPickerProps } from "react-day-picker";
import "react-day-picker/style.css";

import { useShallow } from "zustand/react/shallow";

export const RangePicker = (props: RangePickerProps) => {
  const [hideDatePicker, setHideDatePicker] = useState(true);
  const { onChange, startDate, endDate } = props;
  const dayPickerRef = useRef<HTMLDivElement>(null);
  const [hoveredDate, setHoveredDate] = useHoverStore(
    useShallow((state) => [state.hoveredDate, state.setHoveredDate]),
  );

  const handleChange = useCallback(
    (dates: [Date | null, Date | null]) => {
      if (startDate && endDate) {
        if (dates[0] && dates[1]) {
          if (isSameDay(startDate, dates[0]) && !isSameDay(endDate, dates[1])) {
            return onChange?.([dates[1], null]);
          } else if (!isSameDay(startDate, dates[0]) && isSameDay(endDate, dates[1])) {
            return onChange?.([dates[0], null]);
          }
        }
      }
      onChange?.(dates);

      if (dates[0] && dates[1]) {
        setHideDatePicker(true);
      }
    },
    [onChange, startDate, endDate],
  );

  const locale = locales[i18n.language];

  useEffect(() => {
    const pickerRef = dayPickerRef.current;

    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef && !pickerRef.contains(event.target as Node)) {
        setHideDatePicker(true);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    pickerRef?.addEventListener("mouseleave", () => setHoveredDate(null));
    pickerRef?.addEventListener("keypress", (e) => {
      if (e.key === "Escape") {
        setHideDatePicker(true);
      }
    });

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      pickerRef?.removeEventListener("mouseleave", () => setHoveredDate(null));
      pickerRef?.removeEventListener("keypress", (e) => {
        if (e.key === "Escape") {
          setHideDatePicker(true);
        }
      });
    };
  }, [dayPickerRef, setHoveredDate]);

  useEffect(() => {
    if (!hideDatePicker) {
      const dayPickerPopup = document.getElementsByClassName("daypicker-popup")[0];
      if (window.innerWidth < 768 && dayPickerPopup) {
        dayPickerPopup?.scrollIntoView({ block: "end", behavior: "smooth" });
      }
    } else {
      const daypickerButton = document.getElementsByClassName("c-be-range-picker-wrapper")[0];
      if (window.innerWidth < 768 && daypickerButton) {
        daypickerButton?.scrollIntoView({ block: "start", behavior: "smooth" });
      }
    }
  }, [hideDatePicker]);

  const dayPickerProps = useMemo(
    () =>
      ({
        onSelect: (selected: { from: Date; to: Date } | undefined) =>
          selected && handleChange([selected.from, selected.to]),
        captionLayout: "dropdown",
        mode: "range",
        components: {
          DayButton: DayButton,
        },
        modifiers: {
          inHoveredBoundary: (date: Date) => {
            if (!startDate || !hoveredDate) return false;
            const [start, end] = getDateBoundary([startDate, endDate], hoveredDate);

            return start && end ? isAfter(date, start) && isBefore(date, end) : false;
          },
        },
        numberOfMonths: 2,
        locale,
        modifiersClassNames: {
          inHoveredBoundary: "rdp-range_middle",
        },
        selected: { from: startDate!, to: endDate! },
        defaultMonth: startDate ?? new Date(),
        startMonth: new Date(),
        endMonth: new Date(new Date().setFullYear(new Date().getFullYear() + 2, 11)),
        disabled: props.filterDate ?? filterDateDefault,
        formatters: {
          formatMonthDropdown: (monthNumber: number, locale?: Locale) =>
            capitalize(
              format(new Date().setMonth(monthNumber, 2), "LLLL", {
                locale,
              }),
            ),
        },
        footer: (
          <Footer
            startDate={startDate}
            endDate={endDate}
            onChange={onChange}
            hoveredDate={hoveredDate}
            setHideDatePicker={setHideDatePicker}
          />
        ),
      }) as DayPickerProps,
    [startDate, endDate, handleChange, locale, props.filterDate, onChange, hoveredDate],
  );

  return (
    <div className="c-be-range-picker-wrapper" ref={dayPickerRef}>
      <DayPickerButton
        disabled={!!props.disabled}
        setHideDatePicker={setHideDatePicker}
        startDate={startDate}
        endDate={endDate}
      />
      <div className={`${hideDatePicker ? "daypicker-popup_hide" : "daypicker-popup"}`}>
        <DayPicker {...dayPickerProps} />
      </div>
    </div>
  );
};
